import { useRef, type ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { keyframes, styled } from "@pigment-css/react";
import { Checkmark, CheckmarkThin } from "@acto/web-common/components";
import { SHEET_Z_INDEX } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { scrollIntoClearView } from "@acto/taskpane/field-sales-2/utils/scrollIntoClearView";

const menuIn = keyframes({
  from: { opacity: 0, transform: "translateY(-8px) scale(0.96)" },
  to: { opacity: 1, transform: "translateY(0) scale(1)" },
});

const menuOut = keyframes({
  from: { opacity: 1, transform: "scale(1)" },
  to: { opacity: 0, transform: "scale(0.96)" },
});

type MenuAlign = "start" | "center" | "end";
type MenuSurface = "list" | "capsule";

// Radix DropdownMenu, not Popover: it brings arrow-key nav, roles, focus return and dismissal.
const Surface = styled("div")<{ menuAlign: MenuAlign; surface: MenuSurface }>(({ theme }) => ({
  // Above the sheet layer so a menu opened from inside a BottomSheet is not hidden behind it.
  zIndex: SHEET_Z_INDEX + 1,
  minWidth: "12rem",
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  outline: "none",
  // Radix keeps the node mounted through the close animation via data-state; keyframes (not
  // transitions) so the exit plays before unmount.
  '&[data-state="open"]': { animation: `${menuIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)` },
  '&[data-state="closed"]': { animation: `${menuOut} 120ms ease`, pointerEvents: "none" },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  variants: [
    {
      props: { surface: "list" },
      style: {
        padding: theme.fieldSalesTwo.spacing[8],
        borderRadius: theme.fieldSalesTwo.radius[32],
        background: theme.fieldSalesTwo.colors.surface.glassStrong,
      },
    },
    {
      // Single full-bleed action: the tint carries the destructive weight, and overflow:hidden
      // clips the item's highlight to the rounded ends.
      props: { surface: "capsule" },
      style: {
        padding: 0,
        overflow: "hidden",
        borderRadius: theme.fieldSalesTwo.radius[32],
        background: `linear-gradient(0deg, ${theme.fieldSalesTwo.colors.status.error.tint}, ${theme.fieldSalesTwo.colors.status.error.tint}), ${theme.fieldSalesTwo.colors.surface.glassStrong}`,
      },
    },
    // The scale-in must grow from the anchored edge, so the origin tracks `align`.
    { props: { menuAlign: "start" }, style: { transformOrigin: "top left" } },
    { props: { menuAlign: "center" }, style: { transformOrigin: "top center" } },
    { props: { menuAlign: "end" }, style: { transformOrigin: "top right" } },
  ],
}));

const ItemButton = styled("button")<{ tone: MenuTone; surface: MenuSurface }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  margin: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  outline: "none",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  whiteSpace: "nowrap",
  transition: "background-color 120ms ease",
  "& svg": { flexShrink: 0, width: theme.fieldSalesTwo.spacing[20], height: theme.fieldSalesTwo.spacing[20] },
  // Radix marks a disabled item with data-disabled and already blocks its onSelect + keyboard nav.
  "&[data-disabled]": { opacity: 0.5, cursor: "default", pointerEvents: "none" },
  variants: [
    {
      props: { surface: "list" },
      style: {
        gap: theme.fieldSalesTwo.spacing[12],
        height: theme.fieldSalesTwo.spacing[44],
        padding: `0 ${theme.fieldSalesTwo.spacing[12]}`,
        borderRadius: theme.fieldSalesTwo.radius[16],
        textAlign: "left",
      },
    },
    {
      // Full-bleed: the surface's overflow:hidden clips it, so it carries no radius of its own.
      props: { surface: "capsule" },
      style: {
        gap: theme.fieldSalesTwo.spacing[8],
        height: theme.fieldSalesTwo.spacing[50],
        padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
        borderRadius: 0,
        justifyContent: "center",
      },
    },
    {
      // Radix flags the pointer/keyboard-focused item with data-highlighted.
      props: { tone: "neutral" },
      style: {
        color: theme.fieldSalesTwo.colors.neutral[900],
        "&[data-highlighted]": { backgroundColor: theme.fieldSalesTwo.colors.neutral[200] },
      },
    },
    {
      props: { tone: "destructive" },
      style: {
        color: theme.fieldSalesTwo.colors.status.error.main,
        "&[data-highlighted]": { backgroundColor: theme.fieldSalesTwo.colors.status.error.tint },
      },
    },
  ],
}));

const Trailing = styled("span")({ marginLeft: "auto", display: "flex", alignItems: "center" });

const AccentTrailing = styled(Trailing)(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.accent.action,
  "& svg": { width: 15, height: 15 },
}));

type MenuTone = "neutral" | "destructive";

export interface MenuPopoverItem {
  label: string;
  /** Leading icon; sized to 20px by the item. */
  icon?: ReactNode;
  tone?: MenuTone;
  /** Shows a trailing check. Defining it on any item, even as `false`, renders the whole menu as a single-choice radio group, so assistive technology announces the checked item. */
  selected?: boolean;
  /** Greys the item and blocks its selection — e.g. while a mutation the action triggers is in flight. */
  disabled?: boolean;
  onSelect: () => void;
}

export interface MenuPopoverProps {
  /** The trigger element (kebab, pill, tinted-trash button …); wrapped as the menu's trigger. */
  trigger: ReactNode;
  /** Accessible label for the menu. */
  ariaLabel: string;
  /** Menu alignment relative to the trigger. */
  align?: MenuAlign;
  /** "list": padded rounded card. "capsule": one full-bleed action on a tinted surface. Defaults to "list". */
  surface?: MenuSurface;
  selectedCheckTone?: "neutral" | "accent";
  /** Render open on mount — for stories/tests. */
  defaultOpen?: boolean;
  items: MenuPopoverItem[];
}

/** Opening the menu scrolls its trigger into clear view. */
export function MenuPopover({
  trigger,
  ariaLabel,
  align = "end",
  surface = "list",
  selectedCheckTone = "neutral",
  defaultOpen,
  items,
}: MenuPopoverProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isSelect = items.some((item) => item.selected !== undefined);

  const button = (item: MenuPopoverItem) => (
    <ItemButton tone={item.tone ?? "neutral"} surface={surface} type="button">
      {item.icon}
      {item.label}
      {item.selected &&
        (selectedCheckTone === "accent" ? (
          <AccentTrailing>
            <CheckmarkThin />
          </AccentTrailing>
        ) : (
          <Trailing>
            <Checkmark />
          </Trailing>
        ))}
    </ItemButton>
  );

  const rendered = items.map((item) =>
    isSelect ? (
      <DropdownMenu.RadioItem
        key={item.label}
        value={item.label}
        asChild
        onSelect={item.onSelect}
        disabled={item.disabled}
      >
        {button(item)}
      </DropdownMenu.RadioItem>
    ) : (
      <DropdownMenu.Item key={item.label} asChild onSelect={item.onSelect} disabled={item.disabled}>
        {button(item)}
      </DropdownMenu.Item>
    ),
  );

  return (
    <DropdownMenu.Root
      modal={false}
      defaultOpen={defaultOpen}
      onOpenChange={(open) => {
        if (open) scrollIntoClearView(triggerRef.current);
      }}
    >
      <DropdownMenu.Trigger asChild ref={triggerRef}>
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content asChild side="bottom" align={align} sideOffset={12} aria-label={ariaLabel}>
          <Surface menuAlign={align} surface={surface}>
            {isSelect ? (
              <DropdownMenu.RadioGroup value={items.find((item) => item.selected)?.label ?? ""}>
                {rendered}
              </DropdownMenu.RadioGroup>
            ) : (
              rendered
            )}
          </Surface>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
